// Client data cache to avoid redundant API calls when switching between clients

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  clientName: string;
  ttlMs?: number;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache TTL

class DataCache {
  private cache: Map<string, CacheEntry<any>> = new Map();

  /**
   * Get cached data for a client and endpoint
   */
  get<T>(clientName: string, endpoint: string): T | null {
    const key = `${clientName}:${endpoint}`;
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }
    
    // Check if cache is expired
    const age = Date.now() - entry.timestamp;
    const ttlMs = entry.ttlMs ?? CACHE_TTL;
    if (age > ttlMs) {
      this.cache.delete(key);
      return null;
    }
    
    // Verify it's for the same client
    if (entry.clientName !== clientName) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data as T;
  }

  /**
   * Set cached data for a client and endpoint
   */
  set<T>(clientName: string, endpoint: string, data: T, ttlMs?: number): void {
    const key = `${clientName}:${endpoint}`;
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      clientName,
      ttlMs
    });
  }

  /**
   * Clear cache for a specific client
   */
  clearClient(clientName: string): void {
    const keysToDelete: string[] = [];
    this.cache.forEach((entry, key) => {
      if (entry.clientName === clientName) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache stats (for debugging)
   */
  getStats(): { size: number; entries: Array<{ key: string; age: number; clientName: string }> } {
    const entries = Array.from(this.cache.entries()).map(([key, entry]) => ({
      key,
      age: Date.now() - entry.timestamp,
      clientName: entry.clientName
    }));
    
    return {
      size: this.cache.size,
      entries
    };
  }
}

// Singleton instance
export const dataCache = new DataCache();

// Helper function to create cache key from endpoint
export const getCacheKey = (endpoint: string): string => {
  // Keep meaningful query params (limit/location/includeLargeFields, etc.)
  // and only drop cache-busting timestamp params.
  const [basePath, queryString = ''] = endpoint.split('?');
  if (!queryString) return basePath;

  const params = new URLSearchParams(queryString);
  params.delete('_t');
  params.delete('t');
  params.delete('timestamp');
  params.sort();

  const normalized = params.toString();
  return normalized ? `${basePath}?${normalized}` : basePath;
};

